/**
 * The hard blocks inside `ChangePlanWarningDialog` — one card per block, listed ABOVE the
 * comparison table in the same dialog (Jessica, 2026-10-08). Retrying after a fix re-runs the
 * validation, so a fixed block simply stops being listed; once none are left, the same dialog's
 * "Continue with downgrade" unlocks.
 *
 * Replaces two older surfaces (Jessica, 2026-10-08): the backend's `requiredRemove` message with a
 * bare "Go To" button that named no destination, and the standalone member-limit dialog that only
 * ever showed ONE block — so an account with too many users AND teams fixed the first, retried, and
 * only then met the second. Every block is listed at once, each saying why it blocks and where to
 * fix it.
 *
 * Hard blocks, by `featureType`:
 *  - `allowTeams`            — backend `validateChangePlan` (paid downgrade) or the wizard (Free).
 *  - `shareLink`             — backend; more share links than the target plan allows.
 *  - `memberLimit`           — wizard; Free allows `FREE_PLAN_LIMITS.MAX_USER` users.
 *  - `teamsUpgradeRequired`  — wizard; a locked (expired / cancelled) account with teams. It can't
 *                              reach the teams page, so the card explains and offers no action.
 *  - anything else           — the backend's own message, plus its `url` when it sent one.
 */
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Building2, Link2, TriangleAlert, Users, type LucideIcon } from 'lucide-react';
import { Button } from '@scio3/ui';
import { FREE_PLAN_LIMITS } from '@scio3/shared-types';
import { ACCOUNT_MEMBERS_PATH } from '@/lib/routes';

/** Share Links admin page (`routes/index.tsx` → `share-links-admin`). */
const SHARE_LINKS_ADMIN_PATH = '/app/s/share-links-admin';

export type DowngradeBlocker = {
  featureType?: string;
  messageDisplay?: string;
  plan?: string;
  url?: string;
  /** `memberLimit` only — the account's current user count. */
  count?: number;
};

type BlockerCard = {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: { label: string; path: string; isExternal?: boolean };
};

type Props = {
  blockers: DowngradeBlocker[];
  /** Closes this dialog. The wizard behind it closes itself when a card navigates away. */
  onDismiss: () => void;
};

export function DowngradeBlockers({ blockers, onDismiss }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toCard = (blocker: DowngradeBlocker): BlockerCard => {
    const plan = blocker.plan ?? '';
    switch (blocker.featureType) {
      case 'allowTeams':
        return {
          icon: Building2,
          title: t('Delete your teams'),
          description: t(
            'The {{plan}} Plan does not include teams. Move your screens and content to the Default team, then delete every other team.',
            { plan },
          ),
          action: { label: t('Go to Teams'), path: ACCOUNT_MEMBERS_PATH },
        };
      case 'teamsUpgradeRequired':
        return {
          icon: Building2,
          title: t('Your account has teams'),
          description: blocker.messageDisplay ?? '',
        };
      case 'shareLink':
        return {
          icon: Link2,
          title: t('Delete extra share links'),
          description: t(
            'The {{plan}} Plan allows up to 3 share links. Delete share links until you have 3 or fewer.',
            { plan },
          ),
          action: { label: t('Go to Share Links'), path: SHARE_LINKS_ADMIN_PATH },
        };
      case 'memberLimit':
        return {
          icon: Users,
          title: t('Remove extra users'),
          description: t(
            'The {{plan}} Plan allows up to {{max}} users. Your account has {{count}}.',
            { plan, max: FREE_PLAN_LIMITS.MAX_USER, count: blocker.count ?? 0 },
          ),
          action: { label: t('Go to Users'), path: ACCOUNT_MEMBERS_PATH },
        };
      default:
        return {
          icon: TriangleAlert,
          title: t(blocker.messageDisplay ?? '', { plan }),
          description: '',
          action: blocker.url
            ? { label: t('Go To'), path: blocker.url, isExternal: true }
            : undefined,
        };
    }
  };

  const handleAction = (action: NonNullable<BlockerCard['action']>) => {
    if (action.isExternal) {
      window.open(action.path, '_blank', 'noopener,noreferrer');
      return;
    }
    // Navigate ONLY — do not close the wizard here. `useWizardUrlHash` closes it once `#pricing`
    // leaves the URL. Closing it first made that hook strip the hash with a `replace` built from
    // the Subscription Plan URL it still held, which landed after this navigate and undid it:
    // the pop-ups vanished and the user stayed put.
    onDismiss();
    navigate(action.path);
  };

  return (
    <ul className="space-y-3" data-testid="downgrade-blockers">
      {blockers.map((blocker, i) => {
        const card = toCard(blocker);
        const Icon = card.icon;
        return (
          <li
            key={`${blocker.featureType ?? 'other'}-${i}`}
            className="flex items-start gap-3 rounded-lg border border-border bg-card p-4"
            data-testid={`downgrade-blocker-${blocker.featureType ?? 'other'}`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/10">
              <Icon aria-hidden="true" className="h-4 w-4 text-destructive" />
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="text-sm font-semibold text-foreground">{card.title}</p>
              {card.description && (
                <p className="text-sm text-muted-foreground">{card.description}</p>
              )}
            </div>
            {card.action && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => card.action && handleAction(card.action)}
                data-testid={`downgrade-blocker-${blocker.featureType ?? 'other'}-action`}
              >
                {card.action.label}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
